import React, { useEffect, useState } from "react";
import { Palette, Loader2, Trash2, Check } from "lucide-react";
import { toast } from "sonner";

import {
  fetchToneProfile,
  saveToneProfile,
  deleteToneProfile,
} from "../lib/api";

/**
 * ToneProfileCard — Settings section for the user's saved undertone,
 * skin depth, coverage and finish preferences. When saved, the backend
 * uses these values as a soft-override across all future routines, so
 * the user never has to re-answer the TailorSignalsPanel.
 *
 * Encrypted at rest (Fernet), wiped on account deletion.
 */
const UNDERTONES = [
  { id: "warm", label: "Warm", hint: "golden / yellow / peach" },
  { id: "cool", label: "Cool", hint: "pink / rosy / red" },
  { id: "neutral", label: "Neutral", hint: "a balance of both" },
  { id: "olive", label: "Olive", hint: "greenish-golden" },
];
const DEPTHS = [
  { id: "light", label: "Light" },
  { id: "medium", label: "Medium" },
  { id: "deep", label: "Deep" },
];
const COVERAGES = [
  { id: "sheer", label: "Sheer" },
  { id: "medium", label: "Medium" },
  { id: "full", label: "Full" },
];
const FINISHES = [
  { id: "matte", label: "Matte" },
  { id: "satin", label: "Satin" },
  { id: "dewy", label: "Dewy" },
];

const Pill = ({ active, onClick, children, testid }) => (
  <button
    type="button"
    onClick={onClick}
    className={`px-3.5 py-2 rounded-full text-sm transition-all border ${
      active
        ? "bg-[#9B6A6C] text-white border-[#9B6A6C]"
        : "bg-white/70 text-[#5C4B51] border-[#EADDD7] hover:border-[#D5A8A4]"
    }`}
    data-testid={testid}
  >
    {children}
  </button>
);

const Group = ({ label, optional, children }) => (
  <div>
    <div className="text-xs uppercase tracking-[0.18em] text-[#8A7D82] mb-2">
      {label}
      {optional && (
        <span className="normal-case text-[#BDAFB3] ml-2">· optional</span>
      )}
    </div>
    <div className="flex flex-wrap gap-2">{children}</div>
  </div>
);

const ToneProfileCard = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [undertone, setUndertone] = useState(null);
  const [depth, setDepth] = useState(null);
  const [coverage, setCoverage] = useState(null);
  const [finish, setFinish] = useState(null);
  const [apply, setApply] = useState(true);
  const [hasSaved, setHasSaved] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const p = await fetchToneProfile();
        if (cancelled) return;
        setUndertone(p.undertone);
        setDepth(p.skin_depth);
        setCoverage(p.coverage);
        setFinish(p.finish);
        setApply(p.apply_to_routines !== false);
        setHasSaved(Boolean(p.undertone || p.skin_depth));
        setUpdatedAt(p.updated_at || null);
      } catch (e) {
        if (process.env.NODE_ENV !== "production") {
          // eslint-disable-next-line no-console
          console.debug("[tone-profile] load failed:", e?.message || e);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const canSave = Boolean(undertone || depth || coverage || finish) && !saving;

  const onSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      const p = await saveToneProfile({
        undertone: undertone || null,
        skin_depth: depth || null,
        coverage: coverage || null,
        finish: finish || null,
        apply_to_routines: apply,
      });
      toast.success("Saved. Future routines will match these preferences.");
      setHasSaved(true);
      setUpdatedAt(p.updated_at);
    } catch (e) {
      toast.error("Couldn't save your profile. Try again in a moment.");
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async () => {
    if (!hasSaved || deleting) return;
    setDeleting(true);
    try {
      await deleteToneProfile();
      setUndertone(null);
      setDepth(null);
      setCoverage(null);
      setFinish(null);
      setApply(true);
      setHasSaved(false);
      setUpdatedAt(null);
      toast.success("Profile forgotten.");
    } catch (e) {
      toast.error("Couldn't forget your profile. Try again.");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div
      className="bg-white/80 backdrop-blur-xl border border-[#EADDD7] rounded-3xl p-7 soft-shadow mb-5"
      data-testid="tone-profile-card"
    >
      <div className="flex items-start gap-4 mb-5">
        <div className="shrink-0 h-11 w-11 rounded-full bg-[#F2E8E9] flex items-center justify-center">
          <Palette className="h-5 w-5 text-[#9B6A6C]" />
        </div>
        <div className="flex-1">
          <h3 className="text-xl font-medium text-[#2C2123] leading-tight mb-1">
            My tone profile
            {hasSaved && (
              <span
                className="ml-3 inline-flex items-center gap-1 text-xs uppercase tracking-[0.18em] text-[#9B6A6C] align-middle"
                data-testid="tone-profile-saved-badge"
              >
                <Check className="h-3 w-3" />
                saved
              </span>
            )}
          </h3>
          <p className="text-sm text-[#5C4B51] max-w-xl">
            Save what flatters you once and we'll match products to it on
            every routine. Encrypted on our side, wiped the moment you
            delete your account.
          </p>
        </div>
      </div>

      {loading ? (
        <div
          className="flex items-center gap-2 text-sm text-[#8A7D82]"
          data-testid="tone-profile-loading"
        >
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading…
        </div>
      ) : (
        <div className="space-y-5">
          <Group label="Undertone">
            {UNDERTONES.map((u) => (
              <Pill
                key={u.id}
                active={undertone === u.id}
                onClick={() => setUndertone(undertone === u.id ? null : u.id)}
                testid={`tone-profile-undertone-${u.id}`}
              >
                <span>{u.label}</span>
                <span className="block text-[10px] opacity-80 font-normal -mt-0.5">
                  {u.hint}
                </span>
              </Pill>
            ))}
          </Group>
          <Group label="Skin depth">
            {DEPTHS.map((d) => (
              <Pill
                key={d.id}
                active={depth === d.id}
                onClick={() => setDepth(depth === d.id ? null : d.id)}
                testid={`tone-profile-depth-${d.id}`}
              >
                {d.label}
              </Pill>
            ))}
          </Group>
          <Group label="Coverage" optional>
            {COVERAGES.map((c) => (
              <Pill
                key={c.id}
                active={coverage === c.id}
                onClick={() => setCoverage(coverage === c.id ? null : c.id)}
                testid={`tone-profile-coverage-${c.id}`}
              >
                {c.label}
              </Pill>
            ))}
          </Group>
          <Group label="Finish" optional>
            {FINISHES.map((f) => (
              <Pill
                key={f.id}
                active={finish === f.id}
                onClick={() => setFinish(finish === f.id ? null : f.id)}
                testid={`tone-profile-finish-${f.id}`}
              >
                {f.label}
              </Pill>
            ))}
          </Group>

          <label
            className="flex items-start gap-3 pt-3 border-t border-[#F5EFEB] cursor-pointer select-none"
            data-testid="tone-profile-apply-toggle-label"
          >
            <input
              type="checkbox"
              checked={apply}
              onChange={(e) => setApply(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-[#EADDD7] accent-[#9B6A6C]"
              data-testid="tone-profile-apply-toggle"
            />
            <span className="text-sm text-[#5C4B51]">
              Use this profile for every new routine. Turn off to keep it
              saved for reference without auto-applying.
            </span>
          </label>

          <div className="flex items-center justify-between pt-4 flex-wrap gap-3">
            <div
              className="text-xs text-[#8A7D82]"
              data-testid="tone-profile-updated-at"
            >
              {updatedAt
                ? `Last saved: ${new Date(updatedAt).toLocaleDateString()}`
                : "Not saved yet"}
            </div>
            <div className="flex items-center gap-2">
              {hasSaved && (
                <button
                  type="button"
                  onClick={onDelete}
                  disabled={deleting || saving}
                  className="inline-flex items-center gap-1.5 text-sm text-[#9B6A6C] hover:text-[#805557] disabled:opacity-40 px-3 py-2"
                  data-testid="tone-profile-forget-btn"
                >
                  <Trash2 className="h-4 w-4" />
                  {deleting ? "Forgetting…" : "Forget my profile"}
                </button>
              )}
              <button
                type="button"
                onClick={onSave}
                disabled={!canSave}
                className="inline-flex items-center gap-2 bg-[#9B6A6C] text-white rounded-full px-5 py-2.5 text-sm hover:bg-[#805557] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                data-testid="tone-profile-save-btn"
              >
                {saving ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Check className="h-4 w-4" />
                )}
                {saving ? "Saving…" : "Save my profile"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ToneProfileCard;
